import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@upme/ui/components/alert-dialog';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { CheckCircle, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../../features/auth/auth-context';
import { listCitiesOptions } from '../../../features/cities/queries';
import { listEventCategoriesOptions } from '../../../features/event-categories/queries';
import { EventBroadcastCard } from '../../../features/events/components/event-broadcast-card';
import { EventForm } from '../../../features/events/components/event-form';
import { EventManagerCard } from '../../../features/events/components/event-manager-card';
import { EventPartnerProgramCard } from '../../../features/events/components/event-partner-program-card';
import { EventSourceCard } from '../../../features/events/components/event-source-card';
import { EventStatusActions } from '../../../features/events/components/event-status-actions';
import { formatEventDateTime, STATUS_LABEL, STATUS_VARIANT } from '../../../features/events/lib/format';
import { useApproveEvent, useConvertEventToNews, useRejectEvent } from '../../../features/events/mutations';
import { eventByIdOptions } from '../../../features/events/queries';
import { PageHeader } from '../../../shared/components/page-header';

export const Route = createFileRoute('/_authenticated/events/$eventId')({
  staticData: { breadcrumb: 'Событие' },
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(eventByIdOptions(params.eventId)),
      context.queryClient.ensureQueryData(listCitiesOptions()),
      context.queryClient.ensureQueryData(listEventCategoriesOptions()),
    ]);
  },
  component: EventDetailPage,
});

function EventDetailPage() {
  const router = useRouter();
  const { eventId } = Route.useParams();
  const { data: event } = useSuspenseQuery(eventByIdOptions(eventId));
  const { data: cities } = useSuspenseQuery(listCitiesOptions());
  const { data: categories } = useSuspenseQuery(listEventCategoriesOptions());
  const { adminRoles } = useAuth();
  const isSuperadmin = adminRoles.includes('SUPERADMIN');

  const approve = useApproveEvent(eventId);
  const reject = useRejectEvent(eventId);
  const convertToNews = useConvertEventToNews(eventId);
  const [rejectReason, setRejectReason] = useState('');

  const handleConvertToNews = async () => {
    try {
      await convertToNews.mutateAsync();
      toast.success('Событие перенесено в новости, событие в архиве');
      router.navigate({ to: '/news' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось перенести');
    }
  };

  const handleApprove = async () => {
    try {
      await approve.mutateAsync();
      toast.success('Событие одобрено и опубликовано');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось одобрить');
    }
  };

  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) return;
    try {
      await reject.mutateAsync(reason);
      toast.success('Событие отклонено');
      setRejectReason('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось отклонить');
    }
  };

  const city = cities.find((c) => c.id === event.cityId);
  const tz = city?.timezone ?? 'UTC';

  return (
    <div className="space-y-6">
      <PageHeader
        title={event.title}
        description={
          <span className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <Badge variant={STATUS_VARIANT[event.status]}>{STATUS_LABEL[event.status]}</Badge>
            <span>{city?.name ?? event.cityId}</span>
            <span className="tabular-nums">
              {formatEventDateTime(event.startsAt, tz, event.hasStartTime)}
            </span>
            <span>RSVP: {event.attendanceCount}</span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {event.sourceMessage && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={convertToNews.isPending}>
                    Перенести в новости
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Перенести событие в новости?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Будет создана новость из исходного поста, а это событие уйдёт в архив.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Отмена</AlertDialogCancel>
                    <AlertDialogAction onClick={handleConvertToNews}>Перенести</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <EventStatusActions
              event={event}
              isSuperadmin={isSuperadmin}
              onDeleted={() => router.navigate({ to: '/events' })}
            />
          </div>
        }
      />

      {event.status === 'PENDING_MODERATION' && (
        <div className="rounded-md border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 p-4 mb-6 space-y-3">
          <div className="font-semibold text-amber-900 dark:text-amber-200">
            {event.previouslyPublished
              ? 'Повторная проверка (событие изменено после публикации)'
              : 'Ожидает модерации'}
          </div>
          <div className="flex flex-wrap gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="default" size="sm" disabled={approve.isPending}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Одобрить
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Одобрить событие?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Событие будет опубликовано и станет видно пользователям.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction onClick={handleApprove} disabled={approve.isPending}>
                    {approve.isPending ? 'Одобрение…' : 'Одобрить'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={reject.isPending}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Отклонить
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Отклонить событие?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Событие будет отклонено, организатор получит уведомление с причиной. Монеты будут
                    возвращены.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div>
                  <textarea
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Причина отказа (обязательно)"
                    className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setRejectReason('')}>Отмена</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleReject}
                    disabled={reject.isPending || rejectReason.trim().length === 0}
                  >
                    {reject.isPending ? 'Отклонение…' : 'Отклонить'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      {event.status === 'DRAFT' && event.rejectionReason && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 mb-6 space-y-2">
          <div className="font-semibold text-destructive">Отклонено</div>
          <div className="text-sm">{event.rejectionReason}</div>
        </div>
      )}

      {event.sourceMessage && <EventSourceCard source={event.sourceMessage} />}
      {event.partnerProgram ? (
        <EventPartnerProgramCard
          program={event.partnerProgram}
          price={event.price}
          currency={event.currency}
        />
      ) : null}
      <EventManagerCard event={event} isSuperadmin={isSuperadmin} />
      <EventBroadcastCard eventId={event.id} eventStatus={event.status} />
      <EventForm mode="edit" cities={cities} categories={categories} event={event} />
    </div>
  );
}
